import { Quat } from '@/core/math/Quat';
import { Vec3 } from '@/core/math/Vec3';
import { G } from '@/core/units';
import { TAU } from '@/core/math/scalar';
import { FrameState } from '@/frames/ReferenceFrame';
import { spinAngle } from '@/frames/PlanetaryCoordinates';
import { AtmosphereDef, CelestialBodyDef, VACUUM } from './BodyDef';
import { OrbitalElements, orbitalPeriod, stateVectorAt } from './OrbitalElements';

/**
 * Runtime wrapper around a `CelestialBodyDef`.
 *
 * Derived quantities (mass, mu, sphere of influence, orbital period) are
 * computed once from the definition rather than authored, so the data file
 * cannot contain a body whose mass and surface gravity disagree.
 *
 * Planets are never displaced by anything. Their motion is a pure function of
 * simulation time, which is what makes the universe reproducible and lets
 * analytical time warp jump forward without integrating anything.
 */
export class CelestialBody {
  readonly id: string;
  readonly def: CelestialBodyDef;
  readonly mass: number;
  /** Standard gravitational parameter, m^3/s^2. */
  readonly mu: number;
  readonly radius: number;
  readonly elements: OrbitalElements | null;
  readonly inertialFrameId: string;
  readonly fixedFrameId: string;
  /** Constant rotation taking the equatorial plane away from the ecliptic. */
  readonly tiltRotation: Quat;
  /** Spin axis in the body's inertial frame (unit). */
  readonly spinAxis: Vec3;
  /** Spin rate, rad/s. Signed: negative means retrograde. */
  readonly spinRate: number;

  parent: CelestialBody | null = null;
  readonly children: CelestialBody[] = [];

  /** Sphere-of-influence radius, metres. Infinite for the root star. */
  sphereOfInfluence = Infinity;

  constructor(def: CelestialBodyDef, parentMu: number | null) {
    this.def = def;
    this.id = def.id;
    this.radius = def.radius;
    this.mass = (def.surfaceGravity * def.radius * def.radius) / G;
    this.mu = G * this.mass;
    this.inertialFrameId = def.id;
    this.fixedFrameId = `${def.id}:fixed`;

    this.elements =
      def.orbit && parentMu !== null
        ? {
            ...def.orbit,
            mu: parentMu,
          }
        : null;

    this.tiltRotation = Quat.fromAxisAngle(new Vec3(1, 0, 0), def.axialTilt);
    this.spinAxis = this.tiltRotation.rotate(new Vec3(0, 1, 0)).normalize();
    this.spinRate = def.rotationPeriod === 0 ? 0 : TAU / def.rotationPeriod;
  }

  get name(): string {
    return this.def.name;
  }

  get atmosphere(): AtmosphereDef {
    return this.def.atmosphere ?? VACUUM;
  }

  get hasAtmosphere(): boolean {
    return this.def.atmosphere !== null && this.def.atmosphere.surfaceDensity > 0;
  }

  get explorable(): boolean {
    return this.def.explorable;
  }

  /** Surface gravity at the datum, m/s^2. */
  get surfaceGravity(): number {
    return this.def.surfaceGravity;
  }

  /** Orbital period about the parent, seconds. Infinity when unbound. */
  get orbitalPeriod(): number {
    return this.elements ? orbitalPeriod(this.elements) : Infinity;
  }

  /** Length of one sidereal day, seconds. */
  get dayLength(): number {
    return Math.abs(this.def.rotationPeriod);
  }

  /** Altitude above which the atmosphere is negligible, metres. */
  get atmosphereTop(): number {
    return this.def.atmosphere?.topAltitude ?? 0;
  }

  /** Radius at which a vessel is considered to be in space, metres from centre. */
  get spaceAltitudeRadius(): number {
    return this.radius + this.atmosphereTop;
  }

  /**
   * Pose of this body's inertial frame within its parent's inertial frame.
   * Position and velocity come from analytical Kepler propagation; orientation
   * is identity because an inertial frame does not rotate with its body.
   */
  inertialStateAt(t: number): FrameState {
    if (!this.elements) {
      return {
        position: Vec3.zero(),
        velocity: Vec3.zero(),
        orientation: Quat.identity(),
        angularVelocity: Vec3.zero(),
      };
    }
    const sv = stateVectorAt(this.elements, t);
    return {
      position: sv.position,
      velocity: sv.velocity,
      orientation: Quat.identity(),
      angularVelocity: Vec3.zero(),
    };
  }

  /**
   * Pose of the body-fixed (rotating) frame within this body's inertial frame.
   * The orientation is axial tilt composed with the current spin angle.
   */
  fixedStateAt(t: number): FrameState {
    const angle = spinAngle(t, this.def.rotationPeriod, this.def.epochSpin);
    const spin = Quat.fromAxisAngle(new Vec3(0, 1, 0), angle);
    const orientation = this.tiltRotation.clone().multiply(spin);
    return {
      position: Vec3.zero(),
      velocity: Vec3.zero(),
      orientation,
      angularVelocity: this.spinAxis.clone().mulScalar(this.spinRate),
    };
  }

  /** Gravitational acceleration magnitude at radius `r` from the centre. */
  gravityAt(r: number): number {
    if (r <= 0) return 0;
    // Inside the body, fall off linearly so nothing blows up on a bad clamp.
    if (r < this.radius) return (this.mu / (this.radius * this.radius)) * (r / this.radius);
    return this.mu / (r * r);
  }

  /** Circular orbit speed at radius `r`. */
  circularSpeed(r: number): number {
    return Math.sqrt(this.mu / r);
  }

  /** Escape speed at radius `r`. */
  escapeSpeed(r: number): number {
    return Math.sqrt((2 * this.mu) / r);
  }

  /**
   * Speed of the rotating surface (and, to first order, the atmosphere) at a
   * given planet-fixed position, expressed in the inertial frame.
   */
  surfaceVelocityAt(positionInInertial: Vec3, out = new Vec3()): Vec3 {
    const omega = this.spinAxis.clone().mulScalar(this.spinRate);
    return Vec3.crossVectors(omega, positionInInertial, out);
  }

  /** Computes and caches the sphere-of-influence radius. */
  computeSphereOfInfluence(): void {
    if (!this.parent || !this.elements) {
      this.sphereOfInfluence = Infinity;
      return;
    }
    const ratio = this.mass / this.parent.mass;
    this.sphereOfInfluence = this.elements.semiMajorAxis * Math.pow(ratio, 0.4);
  }
}
